import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

// Cell states: null=empty, GRASS=marked as path (no direction yet), then the 6 pieces, then X
type CellState = null | 'GRASS' | 'LR' | 'UD' | 'UL' | 'UR' | 'DL' | 'DR' | 'X';

const CYCLE: CellState[] = [null, 'GRASS', 'LR', 'UD', 'UL', 'UR', 'DL', 'DR', 'X'];

const PIECE_NAMES = new Set(['LR', 'UD', 'UL', 'UR', 'DL', 'DR']);

// For backend validation: GRASS counts as null (incomplete)
function toValidationPiece(c: CellState): string | null {
  if (c === 'GRASS') return null;
  return c;
}

interface ClueInfo { x: number; y: number; dirs: number; }

interface SenderosLevel {
  width: number; height: number; difficulty: number; seed: number;
  colClues: number[]; rowClues: number[];
  entryRow: number; exitCol: number;
  clues: ClueInfo[];
  solution: (string | null)[][];
}

type Board = CellState[][];

function dirsToPiece(dirs: number): CellState {
  const R = 1, U = 2, L = 4, D = 8;
  const map: Record<number, CellState> = {
    [L | R]: 'LR', [U | D]: 'UD',
    [U | L]: 'DR', [U | R]: 'DL',
    [D | L]: 'UR', [D | R]: 'UL',
  };
  return map[dirs] || null;
}

const PIECE_EDGES: Record<string, { U: boolean; D: boolean; L: boolean; R: boolean }> = {
  'LR': { U: false, D: false, L: true,  R: true  },
  'UD': { U: true,  D: true,  L: false, R: false },
  'UL': { U: false, D: true,  L: false, R: true  },
  'UR': { U: false, D: true,  L: true,  R: false },
  'DL': { U: true,  D: false, L: false, R: true  },
  'DR': { U: true,  D: false, L: true,  R: false },
};

export default function SenderosGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [level, setLevel] = useState<SenderosLevel | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [undoStack, setUndoStack] = useState<Board[]>([]);
  const [redoStack, setRedoStack] = useState<Board[]>([]);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('senderos', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => { loadPuzzle(); }, [dificultadOrden]);

  const loadPuzzle = async (forceNew = false) => {
    try {
      setLoading(true); setError('');
      const urlSeed = !forceNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;
      const response = await api.get('/senderos/generate', {
        params: { dificultad: dificultadOrden, ...(seedToUse && { seed: seedToUse }) }
      });
      const data: SenderosLevel = response.data.data;
      setLevel(data);
      const initial: Board = Array(data.height).fill(null).map(() => Array(data.width).fill(null));
      for (const clue of data.clues) initial[clue.y][clue.x] = dirsToPiece(clue.dirs);
      setBoard(initial);
      setMoves(0); setSolved(false); setShowVictory(false);
      setUndoStack([]); setRedoStack([]);
      window.history.replaceState({}, '', `${window.location.pathname}?seed=${data.seed}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar el puzzle');
    } finally { setLoading(false); }
  };

  const isClueCell = useCallback((x: number, y: number) =>
    level?.clues.some(c => c.x === x && c.y === y) ?? false, [level]);

  const cloneBoard = (b: Board): Board => b.map(r => [...r]);

  const handleCellClick = (x: number, y: number) => {
    if (!board || !level || solved || isClueCell(x, y)) return;
    setUndoStack(prev => [...prev, cloneBoard(board)]);
    setRedoStack([]);
    const newBoard = cloneBoard(board);
    const idx = CYCLE.indexOf(newBoard[y][x]);
    newBoard[y][x] = CYCLE[(idx + 1) % CYCLE.length];
    setBoard(newBoard);
    setMoves(m => m + 1);
    setTimeout(() => checkSolution(newBoard), 200);
  };

  const handleRightClick = (x: number, y: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!board || !level || solved || isClueCell(x, y)) return;
    setUndoStack(prev => [...prev, cloneBoard(board)]);
    setRedoStack([]);
    const newBoard = cloneBoard(board);
    // Right-click toggles X directly: X → empty, anything else → X
    newBoard[y][x] = newBoard[y][x] === 'X' ? null : 'X';
    setBoard(newBoard);
    setMoves(m => m + 1);
  };

  const checkSolution = async (currentBoard: Board) => {
    if (!level) return;
    try {
      const pieces = currentBoard.map(r => r.map(toValidationPiece));
      const { data: { data: { valid } } } = await api.post('/senderos/validate', {
        board: pieces, colClues: level.colClues, rowClues: level.rowClues,
        entryRow: level.entryRow, exitCol: level.exitCol,
        width: level.width, height: level.height,
      });
      if (valid) {
        setSolved(true);
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        try {
          await saveGameProgress({ tablero_id: level.seed, tiempo, movimientos: moves, completado: true });
          await saveSessionResult({ tiempo, movimientos: moves, completado: true,
            datos_juego: { tablero_id: level.seed, size: `${level.width}x${level.height}` } });
        } catch { /* ignore */ }
        setShowVictory(true);
      }
    } catch { /* ignore */ }
  };

  const handleUndo = () => {
    if (!undoStack.length || !board) return;
    setRedoStack(prev => [...prev, cloneBoard(board)]);
    setBoard(undoStack[undoStack.length - 1]);
    setUndoStack(prev => prev.slice(0, -1));
  };

  const handleRedo = () => {
    if (!redoStack.length || !board) return;
    setUndoStack(prev => [...prev, cloneBoard(board)]);
    setBoard(redoStack[redoStack.length - 1]);
    setRedoStack(prev => prev.slice(0, -1));
  };

  const handleHint = async () => {
    if (!board || !level) return;
    try {
      const pieces = board.map(r => r.map(toValidationPiece));
      const { data: { data: { hint } } } = await api.post('/senderos/hint', {
        board: pieces, solution: level.solution, width: level.width, height: level.height,
      });
      if (hint) {
        setUndoStack(prev => [...prev, cloneBoard(board)]);
        const newBoard = cloneBoard(board);
        newBoard[hint.y][hint.x] = hint.value as CellState;
        setBoard(newBoard);
        setMoves(m => m + 1);
      }
    } catch { /* ignore */ }
  };

  const handleReset = () => {
    if (!level || !window.confirm('¿Reiniciar el puzzle?')) return;
    const initial: Board = Array(level.height).fill(null).map(() => Array(level.width).fill(null));
    for (const clue of level.clues) initial[clue.y][clue.x] = dirsToPiece(clue.dirs);
    setBoard(initial); setMoves(0); setSolved(false); setShowVictory(false);
    setUndoStack([]); setRedoStack([]);
  };

  const showSolution = () => {
    if (!level || !window.confirm('¿Mostrar la solución completa?')) return;
    const sol: Board = level.solution.map(r => r.map(p => (p as CellState) ?? null));
    setBoard(sol); setSolved(true);
  };

  const calcErrors = useCallback((): Set<string> => {
    if (!board || !level || !modoAsistido) return new Set();
    const errors = new Set<string>();
    const { width: w, height: h, colClues, rowClues } = level;
    for (let x = 0; x < w; x++) {
      let count = 0;
      for (let y = 0; y < h; y++) if (PIECE_NAMES.has(board[y][x] as string)) count++;
      if (count > colClues[x]) for (let y = 0; y < h; y++) if (PIECE_NAMES.has(board[y][x] as string)) errors.add(`${x},${y}`);
    }
    for (let y = 0; y < h; y++) {
      let count = 0;
      for (let x = 0; x < w; x++) if (PIECE_NAMES.has(board[y][x] as string)) count++;
      if (count > rowClues[y]) for (let x = 0; x < w; x++) if (PIECE_NAMES.has(board[y][x] as string)) errors.add(`${x},${y}`);
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const p = board[y][x];
        if (!p || !PIECE_NAMES.has(p)) continue;
        const edges = PIECE_EDGES[p];
        if (edges.R && x < w-1) { const n = board[y][x+1]; if (n && PIECE_NAMES.has(n) && !PIECE_EDGES[n]?.L) { errors.add(`${x},${y}`); errors.add(`${x+1},${y}`); } }
        if (edges.D && y < h-1) { const n = board[y+1][x]; if (n && PIECE_NAMES.has(n) && !PIECE_EDGES[n]?.U) { errors.add(`${x},${y}`); errors.add(`${x},${y+1}`); } }
        if (edges.L && x === 0 && y !== level.entryRow) errors.add(`${x},${y}`);
        if (edges.R && x === w-1) errors.add(`${x},${y}`);
        if (edges.U && y === 0) errors.add(`${x},${y}`);
        if (edges.D && y === h-1 && x !== level.exitCol) errors.add(`${x},${y}`);
      }
    }
    return errors;
  }, [board, level, modoAsistido]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 to-emerald-800 flex items-center justify-center">
      <div className="text-white text-2xl">🔄 Cargando...</div>
    </div>
  );

  if (!level || !board) return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 to-emerald-800 flex items-center justify-center">
      <div className="text-center text-white space-y-4">
        <div className="text-5xl">🥾</div>
        <div className="text-xl font-semibold">Generando sendero...</div>
        <button onClick={() => loadPuzzle(true)} className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-semibold transition-all">Intentar de nuevo</button>
      </div>
    </div>
  );

  const { width: w, height: h } = level;
  const errors = calcErrors();
  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' }, { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' }, { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];
  const cellWidth = `calc((100vw - 32px) / ${w + 1})`;
  const cellHeight = `calc((100vh - 110px) / ${h + 2})`;
  const fontSize = `min(calc(${cellWidth} * 0.4), calc(${cellHeight} * 0.4))`;

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-green-900 to-emerald-800 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🥾 Senderos</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button key={d.orden} onClick={() => navigate(`/juegos/senderos/${d.orden}`)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${dificultadOrden === d.orden ? 'bg-white text-green-900' : 'bg-white/20 text-white hover:bg-white/30'}`}>
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {[
            { onClick: handleUndo, disabled: !undoStack.length, title: 'Deshacer', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /> },
            { onClick: handleRedo, disabled: !redoStack.length, title: 'Rehacer', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /> },
          ].map(({ onClick, disabled, title, icon }) => (
            <button key={title} onClick={onClick} disabled={disabled} title={title}
              className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>{icon}</svg>
            </button>
          ))}
          <button onClick={() => setModoAsistido(v => !v)} title={modoAsistido ? 'Desactivar asistido' : 'Activar asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-emerald-500 ring-2 ring-emerald-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={solved} title="Pista" className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
          </button>
          <button onClick={showSolution} disabled={solved} title="Solución" className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar" className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
          <button onClick={() => loadPuzzle(true)} title="Nuevo" className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega" className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </button>
        </div>
      </div>

      {error && <div className="mx-3 mt-2 p-2 bg-red-500/20 border border-red-400/50 text-red-200 rounded text-sm">{error}</div>}

      <div className="flex-1 min-h-0 flex items-center justify-center p-2">
        <div className="inline-grid w-full h-full" style={{
          gridTemplateColumns: `${cellWidth} repeat(${w}, ${cellWidth})`,
          gridTemplateRows: `${cellHeight} repeat(${h}, ${cellHeight}) ${cellHeight}`,
          gap: '1px',
        }}>
          <div />
          {level.colClues.map((clue, x) => (
            <div key={`cc-${x}`} className="flex items-center justify-center text-white font-bold bg-black/20 rounded" style={{ fontSize }}>{clue}</div>
          ))}

          {board.map((row, y) => (
            <>
              <div key={`rc-${y}`} className="relative flex items-center justify-center text-white font-bold bg-black/20 rounded" style={{ fontSize }}>
                {y === level.entryRow ? (
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="bg-emerald-500 text-white text-[10px] sm:text-xs font-black px-2 py-0.5 rounded shadow-lg border border-emerald-300">A</div>
                    <span>{level.rowClues[y]}</span>
                  </div>
                ) : level.rowClues[y]}
                {y === level.entryRow && (
                  <div className="absolute right-0 translate-x-[70%] z-10 text-emerald-400 font-black drop-shadow-md" style={{ fontSize }}>▶</div>
                )}
              </div>

              {row.map((cell, x) => {
                const isClue = isClueCell(x, y);
                const hasError = errors.has(`${x},${y}`);
                const isPiece = PIECE_NAMES.has(cell as string);
                const isGrass = cell === 'GRASS';
                const isX = cell === 'X';

                // Background via inline style to avoid Tailwind purge issues
                let bgColor = '#F5F0E1'; // empty: cream
                if (isClue)        bgColor = '#fde68a'; // amber-200: clues (warm, visible)
                else if (hasError)  bgColor = '#fecaca'; // red-200
                else if (isGrass)   bgColor = '#86efac'; // green-300: grass
                else if (isPiece)   bgColor = '#bbf7d0'; // green-200: path with direction

                // Border color
                let borderColor = isClue ? '#b45309' : // amber-700 for clues
                  hasError ? '#ef4444' : // red for errors
                  'rgba(255,255,255,0.15)'; // subtle white for rest

                return (
                  <button key={`${x}-${y}`}
                    onClick={() => handleCellClick(x, y)}
                    onContextMenu={(e) => handleRightClick(x, y, e)}
                    disabled={isClue || solved}
                    style={{
                      width: cellWidth, height: cellHeight,
                      backgroundColor: bgColor,
                      border: `${isClue ? '3px' : '2px'} solid ${borderColor}`,
                    }}
                    className={`relative flex items-center justify-center rounded transition-all overflow-hidden
                      ${!isClue && !solved ? 'cursor-pointer active:scale-95' : 'cursor-default'}`}
                  >
                    {isPiece && (
                      <img src={`/img/senderos/${cell}.png`} alt={cell as string}
                        className="w-full h-full"
                        style={{
                          objectFit: 'fill',
                          // Clue pieces: slightly muted to distinguish from user pieces
                          filter: isClue ? 'saturate(0.4) brightness(0.85)' : undefined,
                        }}
                        draggable={false} />
                    )}
                    {isX && (
                      <span className="text-red-500 font-bold select-none" style={{ fontSize }}>✕</span>
                    )}
                    {isGrass && (
                      // Small green indicator: "path marked, no direction yet"
                      <div style={{ width: '30%', height: '30%', borderRadius: '50%', backgroundColor: '#16a34a', opacity: 0.7 }} />
                    )}
                  </button>
                );
              })}
            </>
          ))}

          <div />
          {level.colClues.map((_, x) => (
            <div key={`bot-${x}`} className="relative flex items-center justify-center">
              {x === level.exitCol && (
                <div className="flex flex-col items-center">
                  <div className="absolute top-0 -translate-y-[70%] z-10 text-emerald-400 font-black drop-shadow-md" style={{ fontSize }}>▼</div>
                  <div className="mt-2 bg-emerald-500 text-white text-[10px] sm:text-xs font-black px-2 py-0.5 rounded shadow-lg border border-emerald-300">B</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <HowToPlayModal isOpen={showHowToPlay} onClose={() => setShowHowToPlay(false)}
        gameTitle="Senderos" gameIcon="🥾"
        objective="Traza un sendero continuo desde la entrada (A) hasta la salida (B). Los números indican cuántos tramos de sendero pasan por cada fila y columna."
        rules={[
          'El sendero debe ir de A (borde izquierdo) a B (borde inferior).',
          'Solo hay tramos rectos y curvos — cada celda con sendero conecta exactamente 2 lados.',
          'El sendero no puede cruzarse ni formar bucles.',
          'Los números de cada fila y columna indican cuántas celdas contienen sendero.',
          'Las celdas con fondo oscuro y borde dorado son pistas fijas dadas por el sistema.',
        ]}
        howToPlay="Toca una celda para avanzar: vacío → 🟢 (marcar como sendero) → dirección horizontal → dirección vertical → 4 curvas → ✕ (sin sendero) → vacío. Click derecho va hacia atrás."
        author="Simon Tatham (adaptado)" />

      <VictoryModal isOpen={showVictory} onClose={() => setShowVictory(false)}
        stats={{ tiempo: Math.floor((Date.now() - startTime) / 1000), movimientos: moves }} />
    </div>
  );
}
